import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { init } from 'crowdhandler-sdk';
import { logger } from './common/logger.js';
import { updateNextPollAnimation, isNextPollAnimating, updateShuffling } from './common/animations';
import formatDate from './formatDate.js';
import { calculateEstimate } from './common/calculateEstimate';
import { formatEta } from './common/formatEta';
import { updateCrowdHandlerWRState } from './utils/stateUtils.js';
import { createCountdown, calculateCountdown } from './common/countdown.js';
import { tokObfuscate } from './utils/tokenObfuscator.js';
import { clearPositionCountdownInterval, applyPoll, resetFirstRun, countdownToZero } from './utils/positionCountdown';
import { validatePriorityCode, isInvalidPriorityCodeError } from './utils/priority';
import { createPromotedSound, PromotedSound } from './utils/promotedSound';
import { checkValidEmailAddress, confirmEmailTerms, checkPreviousEmailMeSubmission, saveEmailSubmissionInSessionStorage } from './utils/emailMe';
// import ChCaptcha from "./components/chCaptcha";
import { calculateProgress } from "./components/calculateProgress.js";
import ChCaptcha from "./components/chCaptcha.js";
import { getJSONLDMetadata } from './common/getJSONLDMetadata.js';
import { getCaptchaTypeFromMetadata } from './common/getCaptchaTypeFromMetadata';
import { CookieEntry, saveTokenEntry, deleteTokenEntry, createCookieStorageSkeleton, getCookieStorageSkeleton, loadTokenEntry, appendSample } from './utils/cookieStorage';
import { formatCrowdHandlerWRState } from './utils/formatCrowdHandlerWRState.js';
import { createI18n } from './i18n/index';

import {
    PositionData,
    CrowdHandlerWRState,
    CrowdHandlerWRSession,
    FormatTimes,
} from './types/crowdhandler';

import {
    chDefaultQueueState,
} from './config/defaults.js';

import { urlParamsParser } from './common/pathFunctions';
import { overlayStoreOrigin, notifyStore, tokenString, READY_MESSAGE, PROMOTED_MESSAGE } from './utils/overlay';
import { applyWakeLock, releaseWakeLock } from './components/applyWakelock.js';
import { applyContrastFallback } from './common/contrastFallback';
import {
    PreviewConfig,
    previewConfigToState,
    simulatePollResponse,
    mergePreviewConfig
} from './utils/previewMode';



/**
 * Main Composable for CrowdHandler Waiting Room Logic
 * Manages the state and interactions for the CrowdHandler Waiting Room integration.
 * @returns {object} The state and methods needed for the template
 */
const CrowdHandlerWR = () => {

    // const debug = import.meta.env.DEV;
    const debug = false;
    const CH_API_ENDPOINT = import.meta.env.MODE === 'dev' || import.meta.env.DEV ? 'https://api-dev.crowdhandler.com' : 'https://api.crowdhandler.com';

    logger(debug, 'info', 'Debug mode enabled');

    // Set when a store frames this waiting room in overlay mode (ch_mode=overlay).
    // Read once, before anything rewrites the URL.
    const overlayOrigin = overlayStoreOrigin(window.location.search);

    // Initialize queue state with defaults
    // The basic session response from CH API
    // Deep copy countdown with inline fallback to ensure it always exists
    const defaultCountdown = {
        seconds: 0,
        minutes: 0,
        hours: 0,
        totalseconds: 999999999,
        onsale: null,
        inHours: null,
        sHours: "--",
        sMinutes: "--",
        sSeconds: "--"
    };
    const ch = ref({
        ...chDefaultQueueState,
        countdown: { ...(chDefaultQueueState.countdown || defaultCountdown) }
    });

    const metadata = getJSONLDMetadata("#crowdhandler-data");

    // i18n: always available, returns English defaults when no language pack loaded
    const i18n = createI18n();
    const { t, dateLocale } = i18n;

    let isPreview: boolean = false;

    let API_KEY: string | undefined;

    // Watch for countdown completion
    watch(() => ch.value.countdown?.totalseconds, (totalseconds) => {
        // Only trigger completion if we're actually in countdown mode
        if (ch.value.isCountdown && totalseconds !== undefined && totalseconds < 0) {
            console.log(`[Countdown Watcher] Countdown complete. isPreview=${isPreview}, status=${ch.value.status}`);
            ch.value.isCountdown = false;
            ch.value.isActive = true;
            // Evaluate shuffling immediately at completion so the user sees
            // "Shuffling positions" the instant the countdown flips. The flip
            // happens at onsale, so now ≈ onsale → within the 60s window →
            // shuffling=true. updateShuffling uses the live clock, so it's
            // correct here even though no fresh poll has happened yet.
            updateShuffling(ch);
            // Start the checking-in progress bar animation only if it isn't
            // already running from prior countdown-phase polls — otherwise we'd
            // reset a healthy animation back to 0 and the bar would jump down.
            if (!isNextPollAnimating()) {
                updateNextPollAnimation(ch, ch.value.pollTTL);
            }
            // In preview mode, transition status from 4 (countdown) to 1 (active)
            if (isPreview && ch.value.status === 4) {
                console.log('[Countdown Watcher] Setting status to 1');
                ch.value.status = 1;
            }
        }
    });


    const title = document.getElementsByTagName('title');
    watch(() => ch.value.position, (newValue) => {
        if (title[0] && ch.value.position) {
            title[0].textContent = `${ch.value.title} ${newValue}`;
        } else if (title[0] && !ch.value.position) {
            title[0].textContent = `${ch.value.title}`;
        }
    }, { deep: true });

    // Dynamic title class based on character count
    watch(() => ch.value.title, (title) => {
        const len = (title || '').length;
        if (len > 40) {
            ch.value.css.title = 'ch-title-xl';
        } else if (len > 25) {
            ch.value.css.title = 'ch-title-lg';
        } else {
            ch.value.css.title = '';
        }
    }, { immediate: true });

    const priorityCode = ref<string | null>(null);

    /* ── Promotion chime ────────────────────────────────────────────────────
       Entirely opt-in: stays null unless the template supplies a
       `promotedSound` URL, so no existing template changes behaviour. */
    let promotedSound: PromotedSound | null = null;
    const SOUND_PREF_KEY = 'ch-promoted-sound';

    let wrGatekeeper: any;
    let positionStore: string | null = null;
    let pollingTimeout: ReturnType<typeof setTimeout> | undefined;
    let currentOnsaleDate: string | null = null;
    let lastAnnouncedMessage: string = "";
    let countdown: any = null;
    let captcha_required: boolean | null = null;
    let captcha_token: string | null = null;
    let captchaType: string | null = null;
    let captchaKey: string | null = null;
    let captcha_clicked: boolean = false;
    let captcha_form: HTMLElement | null = null;
    let statusBeforeCaptcha: number | null = null;
    let is_promoted: boolean = false;

    const teardownCaptchaWidget = () => {
        if (window.ChCaptcha) {
            window.ChCaptcha = null;
        }
        const captchaFormEl = document.getElementById("ch-captcha-form");
        if (captchaFormEl) {
            captchaFormEl.innerHTML = '';
        }
    };

    // Initialize when component mounts
    onMounted(async () => {
        try {
            applyContrastFallback();
            await applyWakeLock(debug);
            await wr_init();
        } catch (error) {
            logger(debug, 'error', `Initialization error: ${error instanceof Error ? error.message : String(error)}`);
            ch.value.isError = true;
        }
    });

    // Clean up when component unmounts
    onUnmounted(() => {
        stopPolling();
        stopPreviewPolling();
        cleanupCountdown();
        clearPositionCountdownInterval();
        resetFirstRun();
        releaseWakeLock();
        if (promotedSound) {
            promotedSound.dispose();
            promotedSound = null;
        }
    });

    // Initialize countdown functionality
    const { updateCountdown, cleanup: cleanupCountdown } = createCountdown({
        ch,
    });

    /**
     * Updates the stock display flags based on the provided data.
     * @param {CrowdHandlerWRState} data - The current waiting room state
     */
    const updateStock = (data: CrowdHandlerWRState) => {
        if (data.stock !== null) {
            ch.value.displayStock = true;
            if (data.stock === 0) {
                ch.value.zeroStock = true;
            }
        } else {
            ch.value.displayStock = false;
            ch.value.zeroStock = false;
        }
    };

    /**
     * Updates the active status of the waiting room session.
     * @param {CrowdHandlerWRState} data - The current waiting room state
     * @param {boolean} value - The new active status
     * @returns {CrowdHandlerWRState} The updated state
     */
    const isActiveHandler = (data: CrowdHandlerWRState, value: boolean) => {
        data = {
            ...data,
            isActive: value
        }
        return data;
    }

    /**
     * Sets the blocked state to true.
     */
    const handleIsBlocked = () => {
        ch.value.isBlocked = true;
    }

    /**
     * Calculates and updates the estimated wait time in the state.
     * @param {CrowdHandlerWRState} data - The current waiting room state
     * @returns {object} The object containing cookie position data
     */
    const handleEstimate = (data: CrowdHandlerWRState) => {

        let estimate: FormatTimes = {
            total_minutes: null,
            hours: null,
            minutes: 0
        };

        const waitTime = data.wait;

        if (waitTime) {
            estimate = {
                ...estimate,
                total_minutes: waitTime,
                hours: Math.floor(waitTime / 60),
                minutes: waitTime % 60
            }
        }


        let cookiePositionData = null;

        if (data.slug && data.token.value) {
            positionStore = `${data.slug}${data.token.value}`;
        }

        if (positionStore) {
            cookiePositionData = loadTokenEntry(positionStore);
        }

        // Resolve the working entry: an existing cookie, or a fresh seed when we
        // first see a real position.
        let entry: CookieEntry | null = cookiePositionData;
        if (!entry && data.position) {
            entry = {
                firstPosition: data.position,
                firstRecorded: Date.now(),
                lastRecorded: Date.now(),
                mostRecentEstimate: null,
                mostRecentPosition: data.position + (data.rate ? data.rate : 0),
                samples: [],
            };
        }

        if (entry && data.position) {
            // Backfill firstPosition if it was never set (e.g. cookie seeded during captcha)
            if (entry.firstPosition === null) {
                entry.firstPosition = data.position;
                entry.firstRecorded = Date.now();
            }

            // Record this poll's real server position in the trailing window the
            // rate calc averages over, pruning anything older than the window.
            entry.samples = appendSample(entry.samples, Date.now(), data.position);

            if (positionStore) {
                saveTokenEntry(positionStore, entry);
            }

            estimate = calculateEstimate(
                { ...entry },
                data.position,
                waitTime
            );

            if (estimate.total_minutes !== null && estimate.total_minutes !== Infinity) {
                data.estimate = estimate.total_minutes;
            } else if (waitTime) {
                data.estimate = waitTime;
            }
        }

        // If no position-based estimate was computed, fall back to waitTime
        if (!data.estimate && waitTime) {
            data.estimate = waitTime;
        }

        if (positionStore && data.position && estimate) {
            // Read-then-write as tight as possible to avoid clobbering
            // mostRecentPosition that the countdown timer may have just written
            const fresh = loadTokenEntry(positionStore);
            if (fresh) {
                fresh.mostRecentEstimate = estimate.total_minutes;
                fresh.lastRecorded = Date.now();
                saveTokenEntry(positionStore, fresh);
            }
        }

        return {
            cpd: cookiePositionData
        }

    }

    /**
     * Manages the countdown state based on the current data.
     * Updates the countdown timer if necessary.
     * @param {CrowdHandlerWRState} data - The current waiting room state
     */
    const handleCountdown = (data: CrowdHandlerWRState) => {
        data.isCountdown = true;
        data.isActive = false;
        if (data.onsale) {
            // Only calculate countdown if we don't have one running
            // or the onsale date has changed
            if (
                (!countdown || !currentOnsaleDate) ||
                (countdown && currentOnsaleDate !== data.onsale)
            ) {
                countdown = calculateCountdown(data.onsale);
                currentOnsaleDate = data.onsale;
                if (countdown) {
                    // Assign calculated countdown directly
                    ch.value.countdown = countdown as any;
                    updateCountdown(data.onsale);
                }
            }
        }
        ch.value = updateCrowdHandlerWRState(ch.value, data);
    }

    /**
     * Updates the queue state with new data from the API.
     * Manages stock, active state, polling, estimates, tokens, and countdowns.
     * @param {CrowdHandlerWRState} data - The new data to update the state with
     */
    const updateCHData = (data: CrowdHandlerWRState) => {

        try {

            updateStock(data)

            // Capture server-provided poll TTL for any status that returns one
            // (countdown, active, etc.). Keeps the polling cadence and the
            // checking-in animation in sync with the server's intent.
            if (!isPreview && data.ttl) {
                ch.value.pollTTL = data.ttl;
            }

            // Active queue status (status 1)
            if (data.status === 1) {
                data = isActiveHandler(data, true)
                const { cpd } = handleEstimate(data);
                if (cpd) {
                    const progressValue = calculateProgress(cpd as PositionData, data.position || 0);
                    // Only update if we get a valid number
                    if (Number.isFinite(progressValue)) {
                        ch.value.progress = progressValue;
                    }
                }

                const currentPosition = ch.value.position || data.position as number; // current position from API
                const nextPosition = data.position as number; // where the API expects you to be at ttl end
                // Optional: server-provided throughput (sessions/min). Not needed for countdown, but can be shown.
                const serverRatePerMin = data.rate as number | null;
                const ttl = ch.value.pollTTL;

                if (positionStore) {
                    applyPoll({
                        ch,
                        token: positionStore,
                        position: currentPosition,
                        result_position: nextPosition,
                        ratePerMinute: serverRatePerMin,
                        ttl,
                    });
                } else if (isPreview) {
                    applyPoll({
                        ch,
                        token: 'preview_token',
                        position: 200,
                        result_position: data.position as number,
                        ratePerMinute: data.rate as number,
                        ttl,
                    });
                }


            }

            if (data.status === 0) {
                handleIsBlocked();
            }

            if (data.token) {
                ch.value.obfuscatedToken = tokObfuscate(data.token.value || "");
            }

            // Handle countdown logic if event is in the future
            // Guard: if API returns status 4 but onsale is already past,
            // treat as active (status 1) to avoid stuck countdown state
            if (data.status === 4 && data.onsale && new Date(data.onsale) <= new Date()) {
                data.status = 1;
                ch.value.isCountdown = false;
            }

            if (data.status === 4) {
                // Default ch.value.status is 1, so a bare `status !== 4` guard
                // also fires on initial load and refresh. Gate cookie-clearing on
                // isLoaded so it only runs after a real API response has set status.
                const wasInActiveQueue = ch.value.isLoaded && ch.value.status === 1;

                if (wasInActiveQueue) {
                    if (positionStore) {
                        deleteTokenEntry(positionStore);
                        positionStore = null;
                    }
                    if (wrGatekeeper) {
                        wrGatekeeper.token = '';
                        wrGatekeeper.simpleCookieValue = undefined;
                        wrGatekeeper.cookieValue = undefined;
                    }
                    document.cookie = 'crowdhandler=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
                    ch.value.position = null;
                    ch.value.estimate = null;
                    ch.value.eta = null;
                    ch.value.etaAt = null;
                    ch.value.progress = 10;
                }

                // Re-enter countdown if either:
                //   - we weren't already in status 4 (fresh entry), or
                //   - we are in status 4 but the post-countdown watcher has
                //     already flipped `isCountdown` to false and the server
                //     has rescheduled the WR with a new onsale date.
                // handleCountdown is idempotent for an unchanged onsale, so
                // it's safe to call when neither branch applies semantically.
                if (ch.value.status !== 4 || !ch.value.isCountdown) {
                    handleCountdown(data);
                    return; // handleCountdown already merges state
                }
            } else {
                ch.value.isCountdown = false;
            }

            // Set active only for status 1 (in queue)
            if (data.status === 1) {
                ch.value.isActive = true;
            }

            // Store the incoming position before potential deletion
            const incomingPosition = data.position;

            // Preserve position if already set (e.g., from animation or cookie)
            // Only use response position if we don't have one yet
            // Note: position 0 is treated as "not set" since queue positions start at 1
            if (ch.value.position) {
                delete (data as any).position;
            }

            ch.value = updateCrowdHandlerWRState(ch.value, data);

            // Fallback: ensure position is set if we have one from the response
            // but it wasn't set by applyPoll or the merge
            if (!ch.value.position && incomingPosition) {
                ch.value.position = incomingPosition;
            }

        } catch (error) {
            console.error('Error updating queue data:', error);
            ch.value.isError = true;
        }
    }

    /**
     * Validates the currently entered email address.
     */
    const validateEmail = (): void => {
        checkValidEmailAddress(ch.value);
    };


    /**
     * Submits the user's email address to the API.
     * Handles terms confirmation and session updates.
     * In preview mode, simulates the submission without making a real request.
     * @returns {Promise<void>}
     */
    const submitEmail = async (): Promise<void> => {
        const response = await confirmEmailTerms();
        if (response !== true) return;

        ch.value.email.submitting = true;

        // Preview mode: simulate submission
        if (isPreview) {
            await new Promise(resolve => setTimeout(resolve, 800));
            ch.value.email.submitted = true;
            ch.value.email.submitting = false;
            ch.value.email.changing = false;
            logger(debug, 'info', `[Preview] Email submitted: ${ch.value.email.value}`);
            return;
        }

        try {
            const success = await makeSessionRequest({ email: ch.value.email.value || '' });
            if (success) {
                saveEmailSubmissionInSessionStorage(`${ch.value.slug}-${ch.value.token?.value}`, ch.value.email.value || '');
                ch.value.email.submitted = true;
                ch.value.email.changing = false;
            }
        } catch (error) {
            logger(debug, 'error', `submitEmail error: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            ch.value.email.submitting = false;
        }
    }

    /**
     * Enters the "changing email" state — shows the form while preserving submitted status.
     */
    const changeEmail = (): void => {
        ch.value.email.changing = true;
        checkValidEmailAddress(ch.value);
    }

    /**
     * Cancels the email change and returns to the submitted confirmation.
     */
    const cancelChangeEmail = (): void => {
        ch.value.email.changing = false;
    }

    /**
     * Obfuscates an email address for display: c****s@c***r.com
     */
    const obfuscateEmail = (email: string): string => {
        if (!email || !email.includes('@')) return email;
        const [local, domain] = email.split('@');
        const [domainName, ...tldParts] = domain.split('.');
        const tld = tldParts.join('.');
        const maskPart = (str: string): string => {
            if (str.length <= 2) return str[0] + '*';
            return str[0] + '*'.repeat(str.length - 2) + str[str.length - 1];
        };
        return `${maskPart(local)}@${maskPart(domainName)}.${tld}`;
    }

    /**
     * Checks if there's a previous email submission stored locally and updates the state.
     * @param {string} token - The session token
     * @param {string} slug - The waiting room slug
     */
    const getEmailMeSubmission = (token: string, slug: string): void => {
        const key = `${slug}-${token}`;
        const response = checkPreviousEmailMeSubmission(key);
        if (response.success) {
            ch.value.email.submitted = true;
            ch.value.email.value = response.email;
        }
    }

    /**
     * Submits a priority code to the API.
     * In preview mode, simulates success for codes starting with "VIP" or "PRIORITY",
     * and simulates failure for other codes.
     */
    const prioritySubmit = async (): Promise<void> => {
        // A code is already in force. syncPriorityFormLock disables the controls,
        // but guard the handler too: a forked template may bind @click to
        // something we don't disable, and re-submitting would spend a request to
        // be told what we already know.
        if (ch.value.priority.applied === true) return;

        ch.value.priority.valid = null;
        ch.value.priority.submitting = true;
        // Clear any prior verdict so the flags below are an honest record of
        // *this* submission (the invalid-code branch in makeSessionRequest
        // sets `error` out-of-band, and we test it after the await).
        ch.value.priority.error = false;
        ch.value.priority.success = false;

        // Preview mode: simulate submission
        if (isPreview) {
            await new Promise(resolve => setTimeout(resolve, 600));
            const code = (ch.value.priority.code || '').toUpperCase();
            // Simulate success for codes starting with VIP or PRIORITY
            const isValidCode = code.startsWith('VIP') || code.startsWith('PRIORITY');

            ch.value.priority.submitted = true;

            if (isValidCode) {
                ch.value.priority.success = true;
                ch.value.priority.error = false;
                ch.value.priority.applied = true;
                // Simulate position improvement
                if (ch.value.position && ch.value.position > 10) {
                    ch.value.position = Math.max(1, Math.floor(ch.value.position * 0.1));
                    ch.value.progress = Math.min(95, ch.value.progress + 40);
                }
                logger(debug, 'info', `[Preview] Priority code accepted: ${code}`);
            } else {
                ch.value.priority.success = false;
                ch.value.priority.error = true;
                ch.value.priority.applied = false;
                logger(debug, 'info', `[Preview] Priority code rejected: ${code}`);
            }

            // Cleared LAST, mirroring the production path where this happens in
            // the `finally` after the response has already set `applied`. Preview
            // used to clear it first, which reversed the order of the final two
            // state changes and hid a real ordering bug in the form lock — so
            // keep this here to stay a faithful test surface.
            ch.value.priority.submitting = false;
            return;
        }

        try {
            const ok = await makeSessionRequest({ code: ch.value.priority.code });

            // The template gates both feedback blocks on `submitted`, so it
            // has to be set on every outcome or the user gets silence.
            ch.value.priority.submitted = true;

            if (ok && !ch.value.priority.error) {
                // Accepted. Nothing else set this in production — the success
                // block used to be reachable in preview mode only.
                //
                // Deliberately NOT setting `applied` here: the response has
                // already been processed by this point (makeSessionRequest
                // awaits manageSessionResponse), so `applied` holds the
                // server's verdict and overwriting it would let us claim a
                // grant the API never made. `success` is the submit-path
                // fallback for a 200 that omits the priority field.
                ch.value.priority.success = true;
            } else if (!ok) {
                // makeSessionRequest swallows API errors (throttle, 5xx,
                // status 6) and returns false to retain state. We can't tell
                // the user the code was wrong, but we mustn't leave the form
                // hanging either.
                ch.value.priority.error = true;
            }
        } catch (error) {
            logger(debug, 'error', `prioritySubmit error: ${error instanceof Error ? error.message : String(error)}`);
            ch.value.priority.submitted = true;
            ch.value.priority.error = true;
        } finally {
            ch.value.priority.submitting = false;
        }
    }

    /**
     * Retrieves and sets the captcha site key from metadata and form element.
     */
    const setCaptchaKey = () => {
        captcha_form = document.getElementById("ch-captcha-form");
        captchaKey = metadata?.captchaKeyPublic || null;
    };

    /**
     * Callback function executed when captcha is successfully solved.
     * @param {string} token - The captcha token
     */
    const captchaCallback = async (token: string) => {
        captcha_clicked = true;
        captcha_token = token;

        // Don't reset captcha after successful validation to avoid timeout errors
        // Just hide it
        ch.value.displayCaptcha = false;

        // Preview mode: simulate captcha success
        if (isPreview && previewConfig) {
            logger(debug, 'info', '[Preview] Captcha completed - simulating success');
            ch.value.captchaRequired = false;

            // Restore the original status from preview config
            const status = previewConfig.status;
            if (status === 4) {
                // Ensure countdown exists before enabling countdown mode
                if (!ch.value.countdown) {
                    ch.value.countdown = { ...defaultCountdown };
                }
                ch.value.isCountdown = true;
                ch.value.isActive = false;
            } else if (status === 3) {
                ch.value.isBlocked = true;
            } else if (status === 5) {
                ch.value.isRoomFull = true;
            } else if (status === 2) {
                ch.value.isError = true;
            } else {
                // Default to active queue (status 1)
                ch.value.isActive = true;

                // Start preview polling if configured
                if (previewConfig.simulatePolling) {
                    startPreviewPolling();
                    updateNextPollAnimation(ch);
                }
            }
            return;
        }

        try {
            const success = await makeSessionRequest({ captcha: captcha_token, captcha_type: captchaType });
            if (success) {
                // Restart polling after successful captcha validation
                startPolling();
            } else {
                // Pseudo-reset: API didn't accept the captcha (most often a
                // rate-limit). Hide the stale widget so the user believes the
                // submission landed, restore the underlying status that was
                // active before captcha was requested, and resume polling.
                // When the API responds again with captchaRequired, the flow
                // in makeSessionRequest will mount a fresh widget.
                updateWRState({ status: 110 });
                updateWRState({ status: statusBeforeCaptcha ?? 1 });
                captcha_required = false;
                captcha_clicked = false;
                captcha_token = null;
                statusBeforeCaptcha = null;
                teardownCaptchaWidget();
                startPolling();
            }
        } catch (error) {
            logger(debug, 'error', `captchaCallback error: ${error instanceof Error ? error.message : String(error)}`);
            ch.value.isError = true;
        }
    };

    /**
     * Initializes the Captcha widget if required.
     * @param {object} params - Parameters for setup
     * @param {string} params.slug - The waiting room slug
     * @param {string} params.token - The session token
     */
    const setupCaptcha = async ({ slug, token }: { slug: string, token: string }) => {
        try {
            // Get captcha type from metadata if not already set (e.g., by preview mode)
            if (!captchaType && metadata && metadata.captchaType) {
                captchaType = getCaptchaTypeFromMetadata(metadata);
            }
            // Default to recaptcha if not set or if metadata returned null
            if (!captchaType) {
                captchaType = 'recaptcha';
            }

            // Get captcha key from metadata if not already set (e.g., by preview mode)
            captcha_form = document.getElementById("ch-captcha-form");
            if (!captchaKey) {
                captchaKey = metadata?.captchaKeyPublic || null;
            }

            if (captchaType === 'recaptcha' && !captchaKey) {
                logger(debug, 'error', 'reCAPTCHA requires sitekey');
                ch.value.isError = true;
                return;
            }

            if (!captcha_form) {
                logger(debug, 'error', 'Captcha form element not found');
                ch.value.isError = true;
                return;
            }

            window.ChCaptcha = new ChCaptcha(
                captcha_form,
                captchaCallback,
                captchaKey,
                captchaType
            );
            await window.ChCaptcha.injectScripts(slug, token);
            logger(debug, 'info', 'Captcha injected');
        } catch (error) {
            logger(debug, 'error', `setupCaptcha error: ${error instanceof Error ? error.message : String(error)}`);
            ch.value.isError = true;
        }
    };

    /**
     * Makes a request to the CrowdHandler API to get the session status.
     * Handles payload construction, priority codes, captcha validation, and error handling.
     * @param {object|null} params - Optional parameters to include in the request
     * @returns {Promise<boolean>} True if request succeeded, false otherwise
     */
    const makeSessionRequest = async (params: object | null = null): Promise<boolean> => {
        let payload: any = {};
        if (priorityCode.value) {
            payload.code = priorityCode.value;
        }
        payload = { ...payload, ...params };

        try {
            // Skip request if captcha is required but not yet completed
            if (captcha_required && !captcha_clicked) {
                logger(debug, 'trace', 'No request made - captcha required');
                return false;
            }

            const result = await wrGatekeeper.validateRequest({
                custom: { ...payload }
            });

            logger(debug, 'info', result);

            if (!result) {
                logger(debug, 'warn', 'No result from validateRequest');
                return false;
            }

            // API error (429, 500, timeout, etc.) — skip this cycle and retain current state.
            // A rejected priority code is a special case handled below, so exclude it here.
            if (result.error && !isInvalidPriorityCodeError(result.error.message)) {
                logger(debug, 'warn', `API error (${result.error.statusCode || 'unknown'}): ${result.error.message} — retaining current state`);
                // Reset `requested` so the recursive startPolling cycle (which
                // bases its delay on requested + pollTTL) gets a clean
                // pollTTL-from-now retry instead of inheriting a stale value
                // from the last successful response and tight-looping.
                ch.value.requested = new Date().toISOString();
                // Keep the checking-in bar cycling between retries.
                updateNextPollAnimation(ch, ch.value.pollTTL);
                return false;
            }

            // Handle invalid priority code
            if (isInvalidPriorityCodeError(result.error?.message)) {
                // Leave ch.value.priority.code alone. Clearing it wiped what
                // the user typed, so the error appeared next to an empty box
                // with no way to spot the typo.
                ch.value.priority.valid = false;
                ch.value.priority.submitted = true;
                ch.value.priority.error = true;
                ch.value.priority.success = false;
                ch.value.priority.applied = false;
                // Drop the rejected code from the URL *and* from the ref that
                // feeds every subsequent poll payload. Stripping only the URL
                // left priorityCode set, so each poll re-sent the bad code,
                // re-entered this branch, and fired a second validateRequest —
                // two requests per cycle and an error that never cleared.
                priorityCode.value = null;
                const url = new URL(window.location.href);
                url.searchParams.delete('ch-code');
                window.history.replaceState({}, '', url.toString());
                // Retry without the invalid code
                await wrGatekeeper.validateRequest();
            }

            // Load email from storage if not submitting a new one
            if (!payload.email) {
                getEmailMeSubmission(result?.token, wrGatekeeper.slug);
            }

            // Read the RAW API session result, NOT validateRequest()'s return
            // value. This is deliberate and load-bearing for a waiting room.
            //
            // validateRequest()'s returned `promoted` is fail-OPEN: on API
            // errors/throttle it resolves to the `trustOnFail` option (default
            // true). That default is correct on the *domain* side (an outage
            // shouldn't block the customer's site). On the *waiting-room* side
            // it's the opposite — promoted means "eject this user back to the
            // domain", so consuming that verdict would flush every waiting
            // visitor to the origin on an API blip: the exact stampede the
            // queue exists to prevent (worsened in SDK 2.5.0, which routes 429s
            // through trustOnFail too).
            //
            // sessionStatus.result.promoted is the raw integer API field,
            // untouched by the trustOnFail branches — so a throttle/outage
            // never fabricates a promotion here. Combined with the explicit
            // status-6 handler below and the catch in makeSessionRequest, a
            // failed poll keeps the user waiting. Do NOT switch this to
            // validateRequest().promoted.
            const response = wrGatekeeper.sessionStatus?.result as CrowdHandlerWRState;

            if (!response) {
                logger(debug, 'error', 'No response from CH_Gatekeeper');
                return false;
            }

            // Status 6: server-side rewrite of a 429 — skip this cycle, retain current state.
            if (response.status === 6) {
                logger(debug, 'warn', 'Rate limited (status 6) — retaining current state');
                // Reset `requested` so the recursive startPolling cycle gets
                // a clean pollTTL-from-now retry instead of computing a
                // zero-delay from a stale last-successful `requested`.
                ch.value.requested = new Date().toISOString();
                // Keep the checking-in bar cycling so the user gets feedback
                // while we wait for the next poll.
                updateNextPollAnimation(ch, ch.value.pollTTL);
                return false;
            }

            // Always set cookie first to ensure we have the most recent token
            // This must happen before handling promotion to ensure the cookie
            // matches the token used in the redirect URL
            if (result?.cookieValue) {
                //
                if (API_KEY && API_KEY === '0445e151608602d8f4707267dd460992448292911fcb857119b8f12924a6a5f9') {
                    wrGatekeeper.setCookie(result?.cookieValue, "https://*.barbican.org.uk");
                } else {
                    try {
                        wrGatekeeper.setCookie(result?.cookieValue);
                    } catch (error) {
                        console.error(error);
                    }
                }
            }

            // Strip ch-id from URL after first successful response so the SDK
            // uses the cookie token on subsequent polls instead of the stale URL token.
            // This also prevents users from sharing URLs with embedded tokens.
            const currentUrl = new URL(window.location.href);
            if (currentUrl.searchParams.has('ch-id')) {
                currentUrl.searchParams.delete('ch-id');
                currentUrl.searchParams.delete('ch-id-signature');
                window.history.replaceState({}, '', currentUrl.toString());
            }

            // Handle promotion — count position to 0 before redirecting.
            // `response` is the raw API result (see note at its declaration):
            // promoted is the integer API field, so `=== 1` only fires on a
            // genuine server-side promotion, never on a fail-open verdict.
            if (response.promoted === 1) {
                logger(debug, 'trace', 'Promoted');
                is_promoted = true;
                stopPolling();

                // Clear the cached position for this slug+token. Once promoted,
                // the session is over — any leftover mostRecentPosition would be
                // stale on the next visit and cause the cookie fallback in
                // wr_init to display a ghost queue position before redirect.
                const tokenValue = typeof response.token === 'string'
                    ? response.token
                    : response.token?.value;
                if (response.slug && tokenValue) {
                    deleteTokenEntry(`${response.slug}${tokenValue}`);
                }
                positionStore = null;

                // Skip the visual countdown-to-zero when either:
                //   - the response omits `rate` (without it we can't judge
                //     whether animating the current position is sensible), or
                //   - the previous position is larger than the admission rate
                //     (the animation would be misleading and, for very large
                //     positions, hits browser setTimeout minimums and runs
                //     much longer than the intended ~5s).
                // Only animate when rate is known AND position fits inside it.
                const rate = typeof response.rate === 'number' ? response.rate : null;
                const currentPos = typeof ch.value.position === 'number' ? ch.value.position : 0;
                const skipCountdown = rate === null || currentPos > rate;
                if (skipCountdown) {
                    handlePromoted(response);
                } else {
                    countdownToZero(ch, () => handlePromoted(response));
                }
                // Return true: the API call *succeeded* — we just won't process
                // further session data because we're redirecting. Returning
                // false here previously caused wr_init's "API-failed" fallback
                // to overwrite ch.value.position with a stale cached value.
                return true;
            }


            // Handle captcha requirement
            if (response.captchaRequired) {
                response.position = null;
                // Capture the underlying queue status so we can restore the
                // right UI if the user solves the captcha but the API rate-
                // limits the validation submission (see captchaCallback).
                statusBeforeCaptcha = typeof response.status === 'number' ? response.status : 1;
                updateWRState({ status: 100 });
                captcha_required = true;
                captcha_clicked = false;
                captcha_token = null;
                stopPolling();
                const captchaSlug = response.slug;
                // response.token from the raw SDK result can be a string, the
                // formatted { value, ... } shape, or null (first-time visitors
                // where the server wants captcha before assigning a token).
                // Falling through to `.value` on null threw a TypeError that
                // killed setupCaptcha — leaving the panel visible but empty.
                const captchaToken = typeof response.token === 'string'
                    ? response.token
                    : (response.token?.value ?? "");

                // Tear down existing captcha instance so a fresh one can be created
                teardownCaptchaWidget();

                // Setup captcha after Vue finishes updates and browser paint
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        setupCaptcha({ slug: captchaSlug, token: captchaToken });
                    }, 50);
                });
            }

            if (!response.captchaRequired) {
                updateWRState({ status: 110 });
            }

            manageSessionResponse(response);

            // Reset nextPoll after response is processed so it stays in sync with actual poll timing
            updateNextPollAnimation(ch, ch.value.pollTTL);

            return true;

        } catch (error: unknown) {
            logger(debug, 'error', `makeSessionRequest error: ${error instanceof Error ? error.message : String(error)}`);

            if (error instanceof Error && error.message === 'Invalid public key') {
                ch.value.isValid = false;
            }

            // Reset `requested` so the next startPolling cycle gets a clean
            // pollTTL-from-now retry rather than inheriting a stale value
            // and tight-looping.
            ch.value.requested = new Date().toISOString();
            // Keep the checking-in bar cycling between retries on thrown
            // errors (e.g. TooManyRequestsException, network failures).
            updateNextPollAnimation(ch, ch.value.pollTTL);

            return false;
        }
    }

    /**
     * Updates the internal waiting room state flags based on the status code.
     * @param {object} params - Parameters
     * @param {number} params.status - The status code from the API
     */
    const updateWRState = ({ status }: { status: number }) => {
        // Reset all state flags before setting the new one
        ch.value.isActive = false;
        ch.value.isError = false;
        ch.value.isBlocked = false;
        ch.value.isCountdown = false;
        ch.value.isRoomFull = false;

        switch (status) {
            case 1:
                ch.value.isActive = true;
                break;
            case 2:
                ch.value.isError = true;
                break;
            case 3:
                ch.value.isBlocked = true;
                break;
            case 4:
                // Ensure countdown exists before enabling countdown mode
                if (!ch.value.countdown) {
                    ch.value.countdown = { ...defaultCountdown };
                }
                ch.value.isCountdown = true;
                break;
            case 5:
                ch.value.isRoomFull = true;
                break;
            case 100:
                ch.value.captchaRequired = true;
                ch.value.displayCaptcha = true;
                break;
            case 110:
                ch.value.captchaRequired = false;
                ch.value.displayCaptcha = false;
                break;
            default:
                break;
        }
    }


    /**
     * Handles the user promotion event.
     * Stops polling, releases resources, sends messages to parent windows/apps, and redirects the user.
     * @param {CrowdHandlerWRState} session - The session data containing the redirect URL
     */
    const handlePromoted = (session: CrowdHandlerWRState) => {


        // release wakelock
        releaseWakeLock();

        // Native wrappers handle their own notification and don't navigate from
        // here, so leave sound to the host app — playing here would either be
        // inaudible or duplicate whatever the app does.
        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage("promoted");
            return;
        }

        if (window.CHMessage) {
            const data = JSON.stringify({
                promoted: 1,
                token: session.token,
            });
            // Send it to Flutter over the "CHMessage" channel
            // The name "CHMessage" must match what's in the Flutter code
            window.CHMessage.postMessage(data);
            return;
        }

        // Send a message to the parent window
        parent.postMessage("promoted", "*");

        // Overlay mode: we are framed on the store page, so hand it the token
        // instead of navigating. The store saves it and reloads itself, which
        // re-checks promotion with the API.
        // An overlay URL opened top-level (new tab, shared link) has nobody to
        // tell, so notifyStore() returns false and we redirect as normal.
        const leaveWaitingRoom = () => {
            const handedOver = !!overlayOrigin && notifyStore({
                type: PROMOTED_MESSAGE,
                token: tokenString(session.token),
                slug: wrGatekeeper?.slug,
            }, overlayOrigin);
            if (!handedOver) wrGatekeeper.redirectIfPromoted();
        };

        /* Hold the redirect just long enough for the chime to be heard.
           redirectIfPromoted() navigates away and navigation kills playback;
           on the skipCountdown path there is otherwise zero time between
           promotion and leaving the page. That's why the chime needs an SDK
           change rather than living in a template.

           This function stays SYNCHRONOUS unless a chime is actually going to
           play. Making it async unconditionally was a mistake: the call sites
           don't await it, so any throw from redirectIfPromoted() turned into an
           unhandled rejection instead of propagating to makeSessionRequest's
           catch — losing the recovery that resets `requested` and keeps the
           poll cycling. That would have applied to every template, including
           the vast majority with no chime configured.

           So: no sound, no promise, identical semantics to before. */
        if (!willPlayPromotedSound()) {
            leaveWaitingRoom();
            return;
        }

        // Chime path only. `finally` so a rejection can't strand the visitor —
        // the redirect happens whatever the audio does. The inner try is
        // deliberate: out here there is no caller left to catch a throw from
        // redirectIfPromoted, so without it a failed redirect would surface as
        // an unhandled rejection and vanish silently.
        playPromotedSound().finally(() => {
            try {
                leaveWaitingRoom();
            } catch (e) {
                logger(debug, 'error', `redirectIfPromoted failed after chime: ${e instanceof Error ? e.message : String(e)}`);
            }
        });
    }

    /* ═══════════════════════════════════════════════════════════════════════
       PROMOTION CHIME

       Gated on the template supplying `promotedSound`. A template that doesn't
       is untouched: no element created, no listeners, no state change.

       Opt-in rather than automatic for three reasons — the autoplay policy
       needs a gesture anyway, WCAG 1.4.2 wants audio under the user's control,
       and an unexpected noise is unpleasant. One click solves all three.
       ═══════════════════════════════════════════════════════════════════════ */

    /**
     * Raw saved preference: '1' opted in, '0' opted out, null never chosen.
     * The three states matter for auto-arm, which treats "never chosen" as on
     * but must still respect an explicit opt-out.
     * Session-scoped, so a new visit starts fresh.
     */
    const readSoundPrefRaw = (): string | null => {
        try { return sessionStorage.getItem(SOUND_PREF_KEY); }
        catch (e) { return null; }   // private mode — just don't persist
    };

    const writeSoundPref = (on: boolean): void => {
        try { sessionStorage.setItem(SOUND_PREF_KEY, on ? '1' : '0'); }
        catch (e) { /* nothing useful to do */ }
    };

    /**
     * Set up the chime if this template asked for one.
     * @param url Resolved, absolute sound URL from template metadata.
     */
    const initPromotedSound = (url: string | undefined): void => {
        if (!url) return;

        promotedSound = createPromotedSound(url);
        if (!promotedSound) return;   // no Audio support

        ch.value.sound.available = true;

        /* Auto-arm: on unless the visitor has explicitly switched it off this
           session. Opt-in via window.CH_promotedSoundAutoArm.

           DEMO USE. It skips the deliberate opt-in, which exists for good
           reasons — WCAG 1.4.2 wants audio under user control, and an
           unrequested noise is unpleasant. Only reasonable where you know a
           gesture is coming (e.g. the visitor is going to type a priority
           code), and where nobody is surprised by the result. Default is off,
           so no template gets this behaviour without asking. */
        const autoArm = window.CH_promotedSoundAutoArm === true;
        const stored = readSoundPrefRaw();
        ch.value.sound.enabled = autoArm ? stored !== '0' : stored === '1';

        // Being enabled is not the same as being unlocked: a fresh document has
        // no user activation, and a stored preference can't grant one. Prime on
        // whatever the visitor does first — a click, or a keystroke in the
        // priority code field. unlock() calls play() synchronously inside this
        // handler, so it lands within the gesture the policy requires.
        if (ch.value.sound.enabled) {
            const prime = async () => {
                if (!promotedSound) return;
                const ok = await promotedSound.unlock();
                ch.value.sound.unlocked = ok;
                if (ok) detach();
            };
            const detach = () => {
                document.removeEventListener('pointerdown', prime);
                document.removeEventListener('keydown', prime);
            };
            document.addEventListener('pointerdown', prime);
            document.addEventListener('keydown', prime);
        }
    };

    /**
     * Toggle the chime. MUST be wired to a real click/keypress — the unlock
     * only succeeds inside a user gesture.
     *
     * Plays the sound when switching on, which doubles as confirmation that
     * audio actually works on this device (silent switch, muted tab, zero
     * volume are all invisible otherwise).
     */
    const toggleSound = async (): Promise<void> => {
        if (!promotedSound || !ch.value.sound.available) return;

        if (ch.value.sound.enabled) {
            ch.value.sound.enabled = false;
            writeSoundPref(false);
            return;
        }

        const ok = await promotedSound.unlock();
        ch.value.sound.unlocked = ok;
        ch.value.sound.enabled = ok;
        writeSoundPref(ok);

        // Deliberately silent. unlock() primes the element with a muted
        // play/pause, so enabling makes no noise — the button's own state change
        // is the confirmation. The trade-off: an audible test clip was the only
        // way to surface a muted tab, zero volume or an iOS silent switch, none
        // of which are detectable in script. A blocked autoplay policy IS still
        // caught, because unlock() fails and the toggle then refuses to turn on.
        if (!ok) {
            logger(debug, 'warn', 'Promotion sound could not be unlocked — audio blocked by the browser');
        }
    };

    /**
     * Play the chime and resolve when it's safe to navigate away.
     * Always resolves — a blocked or stalled clip must never strand the user in
     * the waiting room, so the redirect is only ever delayed, never cancelled.
     */
    /**
     * Will a chime actually play right now?
     *
     * Single source of truth for the conditions, so handlePromoted can stay on
     * its synchronous path whenever the answer is no — which is every template
     * that hasn't configured a sound, i.e. nearly all of them.
     */
    const willPlayPromotedSound = (): boolean =>
        !!promotedSound &&
        ch.value.sound.available &&
        ch.value.sound.enabled &&
        promotedSound.unlocked;

    const playPromotedSound = async (): Promise<void> => {
        if (!willPlayPromotedSound()) return;
        try { await promotedSound!.play(); } catch (e) { /* redirect regardless */ }
    };

    /**
     * Validates the format of the entered priority code.
     * Updates the priority state validity.
     */
    const checkValidPriorityCode = () => {
        let valid = validatePriorityCode(ch.value.priority.code);
        ch.value.priority.valid = valid;
        ch.value.priority.submitted = false;
        ch.value.priority.error = false;
        // Editing the field invalidates the previous verdict — leaving this
        // set would flash a stale "Code applied!" the moment the next
        // submission sets `submitted` again.
        ch.value.priority.success = false;
    }

    /**
     * Locks the priority code field and Apply button once a code is in force,
     * restoring the behaviour of the pre-v2 waiting room.
     *
     * Done from here rather than by adding a `:disabled` binding to the
     * templates because customers deploy forked templates — a markup-only
     * change would never reach them. It works because the two controls are
     * owned differently:
     *
     *   input  — the templates bind v-model and @keyup but NOT :disabled, so
     *            Vue holds no vnode prop for it and never patches it. This
     *            assignment is authoritative.
     *   button — the templates DO bind :disabled. Vue writes that prop whenever
     *            the bound expression's value changes, which will happily
     *            overwrite this one. The write below mirrors the template
     *            expression and ORs the lock in, so unlocking hands control
     *            cleanly back to Vue rather than forcing false over a genuine
     *            `submitting` state — but it only stays put if we re-assert
     *            after every change Vue reacts to. See the watcher below.
     *
     * Re-applied after every response so a v-if remount (captcha → active)
     * that hands us fresh nodes gets locked again.
     */
    const syncPriorityFormLock = (): void => {
        const input = document.getElementById('ch-priority-input') as HTMLInputElement | null;
        if (!input) return;

        const locked = ch.value.priority.applied === true;
        input.disabled = locked;

        // Scoped to the input's own group so a template with several buttons
        // in the section can't have the wrong one disabled.
        const button = input.closest('.ch-input-group')?.querySelector('button');
        if (button) {
            (button as HTMLButtonElement).disabled =
                locked || ch.value.priority.submitting || !ch.value.priority.code;
        }
    };

    // Watch every input to the button's own :disabled expression, not just
    // `applied` — otherwise the lock loses a race.
    //
    // Watching `applied` alone worked in preview mode and failed on a real API
    // response, purely because of statement order in prioritySubmit. The preview
    // branch clears `submitting` BEFORE setting `applied`, so the `applied`
    // change was last and the post-flush lock won. On the production path the
    // response sets `applied`, then `submitting = false` runs in the `finally`
    // afterwards — that later change flips the expression back to false, Vue
    // rewrites disabled=false, and the `applied` watcher never re-fires because
    // `applied` didn't change again. Result: field disabled, button live.
    //
    // Tracking submitting/code too means any change Vue reacts to is followed by
    // our re-assert. flush: 'post' so we land after Vue's DOM patch, not before.
    watch(
        () => [
            ch.value.priority.applied,
            ch.value.priority.submitting,
            ch.value.priority.code
        ],
        () => { syncPriorityFormLock(); },
        { flush: 'post' }
    );

    /**
     * Processes the session response from the API.
     * Formats data, updates state, and triggers UI updates.
     * @param {CrowdHandlerWRSession | CrowdHandlerWRState | void | boolean} session - The session data
     */
    const manageSessionResponse = async (session: CrowdHandlerWRSession | CrowdHandlerWRState | void | boolean) => {

        ch.value.isLoaded = true;

        if (session && typeof session === 'object' && 'status' in session && session.status) {

            if (session && session.slug && session.token && !positionStore) {
                positionStore = `${session.slug}${session.token}`;
            }

            const newSessionData = formatCrowdHandlerWRState(session as CrowdHandlerWRSession, ch.value, i18n);

            updateWRState({ status: session.status });
            updateCHData({ ...newSessionData } as CrowdHandlerWRState);


            // Update screen reader announcement on poll
            // Prepend message on first appearance or change, then just position updates
            let messagePrefix = '';
            if (ch.value.message && ch.value.message !== lastAnnouncedMessage) {
                lastAnnouncedMessage = ch.value.message;
                messagePrefix = ch.value.message + '. ';
            }
            const position = ch.value.position;
            const estimate = ch.value.estimate;
            if (position && estimate) {
                ch.value.announcement = messagePrefix + t('announcement-position-estimate', { position, estimate });
            } else if (position) {
                ch.value.announcement = messagePrefix + t('announcement-position', { position });
            } else if (messagePrefix) {
                ch.value.announcement = messagePrefix;
            }

            // After Vue has rendered this response, re-assert the priority form
            // lock — a remount between polls can hand us freshly created nodes
            // with the field enabled again.
            await nextTick();
            syncPriorityFormLock();
        }
    }

    /**
     * Starts the polling cycle using setTimeout chain.
     * Each poll schedules the next one based on the current ch.value.pollTTL,
     * keeping the interval in sync with the position countdown duration.
     */
    // Seconds elapsed since the last response's server-side `requested`
    // timestamp. Used to schedule the next poll at the cache-expiry wall time
    // so browsers sharing a cached response don't all refire at the same
    // moment and a refreshed browser doesn't wait a full ttl again.
    //
    // Returns 0 (= "wait a fresh full ttl") when:
    //   - no `requested` yet (initial state)
    //   - `requested` is unparseable
    //   - elapsed is negative (client clock skew)
    //   - elapsed is greater than ttl — the server is replaying a cached
    //     response whose `requested` predates the current ttl window.
    //     Without this guard, a stale cache makes `(ttl - elapsed) === 0` and
    //     startPolling fires immediately, producing a tight burst of GETs
    //     (each new response carries the same stale `requested`) until the
    //     cache eventually invalidates and an error response resets it.
    const elapsedSinceRequested = (ttlSec: number): number => {
        if (!ch.value.requested) return 0;
        const requestedMs = Date.parse(ch.value.requested);
        if (Number.isNaN(requestedMs)) return 0;
        const elapsed = (Date.now() - requestedMs) / 1000;
        if (elapsed < 0 || elapsed > ttlSec) return 0;
        return elapsed;
    };

    const startPolling = () => {
        stopPolling();
        const ttl = ch.value.pollTTL;
        const elapsed = elapsedSinceRequested(ttl);
        const delayMs = Math.max(0, (ttl - elapsed) * 1000);

        logger(debug, 'info', `Delaying poll by ${delayMs}ms (ttl: ${ttl}, elapsed: ${elapsed})`);
        pollingTimeout = setTimeout(async () => {
            try {
                await makeSessionRequest();
            } catch (error) {
                logger(debug, 'error', `Polling error: ${error instanceof Error ? error.message : String(error)}`);
            }
            // Once promoted, stop polling entirely — handlePromoted will redirect
            // and any further poll would be wasted work. If the redirect is
            // delayed (e.g. ReactNative/Flutter postMessage path that returns
            // without navigating), polling would otherwise re-trigger the
            // promoted branch in a loop.
            if (is_promoted) {
                return;
            }
            // Don't keep polling while a captcha is pending the user's solve.
            // The guard inside makeSessionRequest short-circuits each call, so
            // the loop becomes a silent busy-noop; worse, the moment the user
            // solves the captcha the next pending tick fires a concurrent
            // request with the pre-captcha cookie. captchaCallback restarts
            // polling on success or pseudo-reset.
            if (!captcha_required || captcha_clicked) {
                startPolling();
            }
        }, delayMs);
    }

    /**
     * Stops the polling cycle.
     */
    const stopPolling = () => {
        if (pollingTimeout !== undefined) {
            clearTimeout(pollingTimeout);
            pollingTimeout = undefined;
        }
    }


    let previewConfig: PreviewConfig | null = null;
    let previewPollingInterval: NodeJS.Timeout | null = null;

    /**
     * Kicks off a preview applyPoll cycle so position counts down immediately.
     */
    const startPreviewCountdown = () => {
        if (!previewConfig) return;
        const interval = previewConfig.pollInterval || 5000;
        const decrement = previewConfig.positionDecrement || 5;
        const ttl = Math.round(interval / 1000);
        const currentPos = ch.value.position ?? previewConfig.position ?? 150;
        const targetPos = Math.max(1, currentPos - decrement);

        applyPoll({
            ch,
            token: 'preview_token',
            position: currentPos,
            result_position: targetPos,
            ratePerMinute: decrement,
            ttl,
        });
    };

    /**
     * Starts simulated polling for preview mode.
     * Mirrors production: position counts down between polls (via applyPoll),
     * progress and estimate only update when a "poll" fires.
     */
    const startPreviewPolling = () => {
        if (!previewConfig?.simulatePolling) return;

        const interval = previewConfig.pollInterval || 5000;
        const decrement = previewConfig.positionDecrement || 5;
        const ttl = Math.round(interval / 1000);

        // Start position countdown immediately (don't wait for first poll)
        startPreviewCountdown();

        previewPollingInterval = setInterval(() => {
            if (ch.value.status !== 1 || !ch.value.isActive) return;

            // Simulate a poll response — progress and estimate jump here
            const updates = simulatePollResponse(ch.value, previewConfig!);
            ch.value.progress = updates.progress ?? ch.value.progress;
            ch.value.estimate = updates.estimate ?? ch.value.estimate;
            ch.value.requested = updates.requested ?? ch.value.requested;

            // Start next position countdown cycle
            const currentPos = ch.value.position ?? updates.position ?? 150;
            const targetPos = Math.max(1, currentPos - decrement);

            applyPoll({
                ch,
                token: 'preview_token',
                position: currentPos,
                result_position: targetPos,
                ratePerMinute: decrement,
                ttl,
            });

            // Update announcement for screen readers
            if (ch.value.position && ch.value.estimate) {
                ch.value.announcement = t('announcement-position-estimate', { position: ch.value.position, estimate: ch.value.estimate });
            }

            // Animate the poll bar
            updateNextPollAnimation(ch, ttl);

            logger(debug, 'info', `[Preview] Simulated poll: position=${ch.value.position}, progress=${ch.value.progress}%`);
        }, interval);
    };

    /**
     * Stops preview polling
     */
    const stopPreviewPolling = () => {
        if (previewPollingInterval) {
            clearInterval(previewPollingInterval);
            previewPollingInterval = null;
        }
    };

    // Watch for status transition to start preview polling (e.g., after countdown completion)
    watch(() => ch.value.status, (newStatus, oldStatus) => {
        console.log(`[Status Watcher] status changed: ${oldStatus} → ${newStatus}, isPreview=${isPreview}, simulatePolling=${previewConfig?.simulatePolling}`);
        if (isPreview && previewConfig?.simulatePolling && newStatus === 1 && oldStatus === 4) {
            console.log('[Status Watcher] Starting preview polling after countdown');
            // Start with null position (shows "calculating")
            ch.value.position = null;
            ch.value.progress = 0;
            // Start polling and animation
            startPreviewPolling();
            updateNextPollAnimation(ch);
        }
    });

    /**
     * Initializes the Waiting Room logic.
     * Loads metadata, sets up languages, initializes the SDK gatekeeper, and starts the process.
     */
    const wr_init = async () => {

        // Overlay mode: tell the store we loaded, or it falls back to redirecting here.
        notifyStore({ type: READY_MESSAGE }, overlayOrigin);

        // Get room data from crowdhandler-data (always needed for base config)
        let roomMetadata = getJSONLDMetadata('#crowdhandler-data');

        // Check for preview mode via dev flag in crowdhandler-data
        isPreview = roomMetadata?.dev === true;
        previewConfig = isPreview ? mergePreviewConfig(roomMetadata!) : null;
        await nextTick();

        // Fetch CDN language pack early (non-blocking until awaited). This is a
        // no-op unless the template carries data-ch-i18n — standard and custom
        // templates must never make a request to the i18n CDN.
        const detectedLang = i18n.locale.value;
        const cdnBaseUrl = roomMetadata?.i18nBaseUrl || undefined;
        const cdnFetchPromise = i18n.fetchCdnPack(detectedLang, cdnBaseUrl);

        // Customer overrides always win — load them immediately
        if (window?.CH_languages) {
            i18n.loadPacks(window.CH_languages);
        }

        // Promotion chime, if this template opted in. Set up before the
        // preview/production split so it works in both, and early enough that
        // the clip is decoded long before any promotion can occur.
        //
        // Read from window.CH_promotedSound, not from room metadata: the
        // crowdhandler-data block is regenerated server-side at render time, so
        // a template-authored key there is discarded before the SDK ever sees
        // it. Same pattern as window.CH_languages.
        //
        // Room metadata is still checked first, so this can become a real
        // per-room setting later without touching any template.
        initPromotedSound(roomMetadata?.promotedSound || window.CH_promotedSound);

        // Wait for CDN fetch before first render (fails silently → English defaults)
        await cdnFetchPromise;

        // ========== PREVIEW MODE ==========
        if (isPreview && previewConfig) {
            logger(debug, 'info', '[Preview Mode] Enabled - using preview data');

            // Merge preview config with any base room metadata
            const baseData = roomMetadata || {};
            const mergedConfig = mergePreviewConfig({
                ...baseData,
                ...previewConfig,
            });

            // Convert to state format
            const previewState = previewConfigToState(mergedConfig);


            console.log(`[Preview] Preview state: ${JSON.stringify(previewState, null, 4)}`);

            // Apply state flags based on status
            updateWRState({ status: mergedConfig.status });

            // Handle countdown state — default to 1 hour from now if no onsale provided
            if (mergedConfig.status === 4 && !mergedConfig.onsale) {
                mergedConfig.onsale = new Date(Date.now() + 60 * 60 * 1000).toISOString();
            }
            if (mergedConfig.status === 4 && mergedConfig.onsale) {
                ch.value.isActive = false;
                const countdown = calculateCountdown(mergedConfig.onsale);
                if (countdown) {
                    // Assign calculated countdown directly
                    ch.value.countdown = countdown as any;
                    updateCountdown(mergedConfig.onsale);
                }
                // Note: updateWRState already sets isCountdown=true and ensures countdown exists
            }

            // Handle captcha state
            if (mergedConfig.captchaRequired) {
                ch.value.captchaRequired = true;
                ch.value.displayCaptcha = true;

                // Set captcha config from preview data
                captchaType = mergedConfig.captchaType || 'recaptcha';
                captchaKey = mergedConfig.captchaKeyPublic || null;

                // Setup captcha after Vue updates DOM
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        setupCaptcha({
                            slug: mergedConfig.slug || 'preview',
                            token: mergedConfig.token || 'preview-token'
                        });
                    }, 50);
                });
            }

            // Update main state
            ch.value = updateCrowdHandlerWRState(ch.value, previewState as CrowdHandlerWRState);
            // Apply progress directly (updateCrowdHandlerWRState preserves existing progress for production mode)
            if (previewState.progress !== undefined) {
                ch.value.progress = previewState.progress;
            }
            ch.value.requested = new Date().toISOString();
            ch.value.isLoaded = true;

            // Start simulated polling if enabled and in active state
            if (mergedConfig.simulatePolling && mergedConfig.status === 1) {
                const ttl = Math.round((mergedConfig.pollInterval || 5000) / 1000);
                startPreviewPolling();
                updateNextPollAnimation(ch, ttl);
            }

            logger(debug, 'info', `[Preview Mode] State initialized: ${ch.value}`);
            return;
        }

        // ========== PRODUCTION MODE ==========
        if (!roomMetadata) {
            console.error('No roomMetadata found');
            return;
        }

        // Seed estimate from template metadata so it's visible before first API response
        if (roomMetadata.wait) {
            ch.value.estimate = roomMetadata.wait;
        }

        // Create initial view from template data
        if (roomMetadata && roomMetadata.status) {
            const newSessionData = formatCrowdHandlerWRState(roomMetadata as CrowdHandlerWRSession, ch.value, i18n);
            updateWRState({ status: roomMetadata.status });
            updateCHData({ ...newSessionData } as CrowdHandlerWRState);
            ch.value.isLoaded = true;
        }

        // Get code if it exists in url
        priorityCode.value = urlParamsParser("ch-code");

        if (!roomMetadata.key) {
            console.error('No API key found');
            return;
        }

        if (!getCookieStorageSkeleton("crowdhandler_positions")) {
            createCookieStorageSkeleton("crowdhandler_positions");
        }

        API_KEY = roomMetadata.key;

        const config = {
            publicKey: roomMetadata.key,
            options: {
                debug: false,
                apiUrl: CH_API_ENDPOINT,
                waitingRoom: true,
                // Persist the queue token for 24h so a returning visitor
                // keeps their place after a browser close. SDK default is
                // a session cookie.
                cookieMaxAgeSeconds: 86400
            }
        }

        logger(debug, 'info', `Config: ${JSON.stringify(config)}`);

        const { gatekeeper, client } = init(config);

        if (!gatekeeper) {
            console.error('Gatekeeper not created');
            return;
        }

        if (!client) {
            console.error('Client not created');
            return;
        }
        wrGatekeeper = gatekeeper;
        const initialSuccess = await makeSessionRequest();

        // If the initial API call failed (429, network error, etc.) and we have no position,
        // fall back to the last-known state from the crowdhandler_positions cookie.
        if (!initialSuccess && !ch.value.position) {
            const positionsCookie = getCookieStorageSkeleton("crowdhandler_positions");
            if (positionsCookie?.positions) {
                const slug = wrGatekeeper.slug;

                // A user may have multiple cookie entries for this slug — one
                // per queue session/token they've ever joined. Pick the entry
                // with the most recent lastRecorded, which represents the
                // session they were most recently active in.
                let best: { key: string; entry: CookieEntry } | null = null;
                for (const [key, rawEntry] of Object.entries(positionsCookie.positions)) {
                    if (!key.startsWith(slug)) continue;
                    const entry = rawEntry as CookieEntry;
                    if (!entry.mostRecentPosition) continue;
                    if (!best || (entry.lastRecorded ?? 0) > (best.entry.lastRecorded ?? 0)) {
                        best = { key, entry };
                    }
                }

                if (best) {
                    ch.value.position = best.entry.mostRecentPosition;
                    ch.value.estimate = best.entry.mostRecentEstimate;
                    ch.value.isActive = true;
                    positionStore = best.key;
                    logger(debug, 'info', `Falling back to cached position: ${best.entry.mostRecentPosition}`);
                }
            }
        }

        startPolling();
    }



    // ETA: recalculate on every poll (requested changes each time, even if estimate doesn't).
    // etaAt is the raw moment for templates that format it themselves; ch.eta is the
    // legacy pre-formatted default (locale time when <=60 min, null otherwise).
    watch(() => ch.value.requested, () => {
        const estimate = ch.value.estimate;
        // Raw ETA moment as an ISO string (like ch.requested / ch.onsale), for
        // templates to format themselves via formatDate(). ch.eta stays as a
        // pre-formatted, non-breaking default for templates that don't.
        const atMs = estimate && estimate >= 1 ? Date.now() + estimate * 60000 : null;
        ch.value.etaAt = atMs ? new Date(atMs).toISOString() : null;
        ch.value.eta = formatEta(estimate, ch.value.etaAt, dateLocale.value);
    });

    // Return all the data and methods needed in the template
    return {
        ch,
        // i18n
        t,
        dateLocale,
        // Methods
        // Bind the room's locale so templates call formatDate(date, format) with
        // no locale arg and still get region-correct, language-consistent output.
        formatDate: (date: string | Date, format: string | Function = 'toLocaleString') =>
            formatDate(date, format, dateLocale.value),
        submitEmail,
        validateEmail,
        changeEmail,
        cancelChangeEmail,
        obfuscateEmail,
        prioritySubmit,
        checkValidPriorityCode,
        priorityCode,
        // Promotion chime. Inert unless the template supplies `promotedSound`;
        // templates gate their control on ch.sound.available.
        toggleSound,
    };
}

export { CrowdHandlerWR };
